import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import './LazyChunkSuspense.css';

type LazyChunkSuspenseProps = {
  children: React.ReactNode;
  /** Fallback pendant le chargement du chunk (null = rien). */
  fallback?: React.ReactNode;
  /** Message court pour le fallback par défaut (i18n si omis). */
  label?: string;
};

type ErrorBoundaryProps = {
  children: React.ReactNode;
  resetKey: number;
  onRetry: () => void;
  errorMessage: string;
  retryLabel: string;
};

type ErrorBoundaryState = { hasError: boolean };

class LazyChunkErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className='lazy-chunk-fallback lazy-chunk-fallback--error' role='alert'>
          <p className='lazy-chunk-fallback__text'>{this.props.errorMessage}</p>
          <button type='button' className='lazy-chunk-fallback__retry' onClick={this.props.onRetry}>
            {this.props.retryLabel}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const DefaultLazyFallback: React.FC<{ label: string }> = ({ label }) => (
  <div className='lazy-chunk-fallback' aria-busy='true' aria-live='polite' role='status'>
    {label}
  </div>
);

/** Suspense + fallback visible + retry si le chunk lazy échoue. */
export function LazyChunkSuspense({ children, fallback, label }: LazyChunkSuspenseProps) {
  const { t } = useTranslation('common');
  const [resetKey, setResetKey] = React.useState(0);
  const loadingLabel = label ?? t('lazyChunk.loading');
  const errorMessage = t('lazyChunk.loadError');
  const retryLabel = t('lazyChunk.retry');

  return (
    <LazyChunkErrorBoundary
      resetKey={resetKey}
      onRetry={() => setResetKey(k => k + 1)}
      errorMessage={errorMessage}
      retryLabel={retryLabel}
    >
      <Suspense
        fallback={
          fallback !== undefined ? fallback : <DefaultLazyFallback label={loadingLabel} />
        }
      >
        {children}
      </Suspense>
    </LazyChunkErrorBoundary>
  );
}

export default LazyChunkSuspense;
